import { z } from "zod";
import { UiColorSchema, UiFont } from "./entities.ts";
import { TokenNameSchema, TokenRefSchema, isTokenRef, type TokenRef } from "./tokenRef.ts";

export { TokenNameSchema, TokenRefSchema, isTokenRef, type TokenRef };

export const ThemeColorTokenSchema = z
	.object({
		kind: z.literal("color"),
		value: UiColorSchema,
	})
	.strict();
export type ThemeColorToken = z.infer<typeof ThemeColorTokenSchema>;

export const ThemeNumberTokenSchema = z
	.object({
		kind: z.literal("number"),
		value: z.number().finite(),
	})
	.strict();
export type ThemeNumberToken = z.infer<typeof ThemeNumberTokenSchema>;

export const ThemeFontTokenSchema = z
	.object({
		kind: z.literal("font"),
		value: UiFont,
	})
	.strict();
export type ThemeFontToken = z.infer<typeof ThemeFontTokenSchema>;

export const ThemeShadowTokenSchema = z
	.object({
		kind: z.literal("shadow"),
		value: z
			.object({
				color: UiColorSchema,
				blur: z.number().finite().min(0).max(128),
				spread: z.number().finite().min(-128).max(128),
				offsetX: z.number().finite().min(-128).max(128),
				offsetY: z.number().finite().min(-128).max(128),
				transparency: z.number().finite().min(0).max(1),
				inset: z.boolean(),
			})
			.strict(),
	})
	.strict();
export type ThemeShadowToken = z.infer<typeof ThemeShadowTokenSchema>;

export const ThemeGradientTokenSchema = z
	.object({
		kind: z.literal("gradient"),
		value: z
			.object({
				stops: z
					.array(
						z
							.object({
								color: UiColorSchema,
								position: z.number().finite().min(0).max(1),
							})
							.strict(),
					)
					.min(2)
					.max(16),
				rotation: z.number().finite().min(0).max(360),
			})
			.strict(),
	})
	.strict();
export type ThemeGradientToken = z.infer<typeof ThemeGradientTokenSchema>;

export const ThemeTokenSchema = z.discriminatedUnion("kind", [
	ThemeColorTokenSchema,
	ThemeNumberTokenSchema,
	ThemeFontTokenSchema,
	ThemeShadowTokenSchema,
	ThemeGradientTokenSchema,
]);
export type ThemeToken = z.infer<typeof ThemeTokenSchema>;

export type ThemeTokenKind = ThemeToken["kind"];

export const ThemeTokensSchema = z
	.record(z.string(), ThemeTokenSchema)
	.superRefine((tokens, ctx) => {
		for (const name of Object.keys(tokens)) {
			const tokenName = TokenNameSchema.safeParse(name);
			if (!tokenName.success) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [name],
					message: "Token name must be lowercase dot-separated (e.g. text.primary)",
				});
			}
		}
	});

export const ThemeSchema = z
	.object({
		tokens: ThemeTokensSchema,
	})
	.strict();
export type Theme = z.infer<typeof ThemeSchema>;

export const CmsThemeUpdateBody = ThemeSchema.partial();
export type CmsThemeUpdate = z.infer<typeof CmsThemeUpdateBody>;

const color = (r: number, g: number, b: number): ThemeColorToken => ({
	kind: "color",
	value: { r, g, b },
});

const font = (v: z.infer<typeof UiFont>): ThemeFontToken => ({
	kind: "font",
	value: v,
});

const number = (v: number): ThemeNumberToken => ({
	kind: "number",
	value: v,
});

export const DEFAULT_THEME_TOKENS: Readonly<Record<string, ThemeToken>> = Object.freeze({
	"color.primary": color(80, 150, 255),
	"color.secondary": color(255, 220, 100),
	"color.success": color(50, 160, 80),
	"color.warning": color(220, 140, 60),
	"color.error": color(220, 80, 80),

	"color.background": color(20, 24, 36),
	"color.surface": color(35, 40, 56),

	"color.text": color(255, 255, 255),
	"color.text.dim": color(160, 165, 190),

	"font.body": font("Gotham"),
	"font.heading": font("GothamBold"),
	"font.label": font("GothamSemibold"),

	"radius.md": number(8),
	"text.size.md": number(14),
	"text.size.lg": number(18),
});

export const DEFAULT_THEME: Theme = {
	tokens: DEFAULT_THEME_TOKENS,
};
